import { isHex, normalizeHex } from './color'

/**
 * Registry search over an in-memory list: the offline fallback for /api/search,
 * with the same contract (LIKE filter, relevance = Levenshtein distance,
 * sortable columns, limit/offset).
 */

export type ColorType = 'reserved' | 'registered_public' | 'registered_custom' | 'registered_generated'

export interface RegistryColor {
  /** Registration number (the database id). Absent for sample/fallback data. */
  regNum?: number
  name: string
  hex: string
  slug: string
  type: ColorType
  /** ISO date, null for reserved colors */
  registration: string | null
  /** ISO date, null = lifetime (or reserved) */
  expiration: string | null
}

export type SearchType = 'name' | 'hex'
export type SortColumn = 'name' | 'hex' | 'reg' | 'exp'
export type SortOrder = 'relevant' | `${SortColumn}-${'asc' | 'desc'}`

export interface SearchParams {
  query: string
  type: SearchType
  order?: SortOrder
  limit?: number
  offset?: number
}

export interface SearchResult {
  search: boolean
  query: string
  type: SearchType
  order: SortOrder
  limit: number
  offset: number
  count: number
  /** True when the count stopped at the server's cap (10,000). */
  capped?: boolean
  timer: number
  results: RegistryColor[]
}

export function levenshtein(a: string, b: string): number {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    const cur = [i]
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
    }
    prev = cur
  }
  return prev[b.length]
}

/** Input scrubbing applied while typing. */
export function sanitizeHexInput(raw: string): string {
  let v = raw.toLowerCase().replace(/#{2,}/g, '#')
  if (!v.startsWith('#')) v = '#' + v
  v = v.replace(/[^a-f0-9#]/g, '')
  return v.slice(0, 7)
}

export function sanitizeNameInput(raw: string): string {
  return raw.replace(/[^a-z\s]/gi, '').slice(0, 100)
}

/** True when the query is worth searching. */
export function isSearchable(query: string, type: SearchType): boolean {
  if (type === 'hex') return /^#[0-9a-f]/i.test(query) && query.length > 1
  return /[a-z]/i.test(query) && query.trim().length > 1
}

function cmp(a: string | null, b: string | null): number {
  if (a === b) return 0
  if (a === null) return 1
  if (b === null) return -1
  return a < b ? -1 : 1
}

export function searchColors(colors: RegistryColor[], params: SearchParams): SearchResult {
  const start = performance.now()
  const type = params.type
  const limit = Math.min(Math.max(params.limit ?? 10, 1), 50)
  const offset = Math.max(params.offset ?? 0, 0)
  const order: SortOrder = params.order ?? 'relevant'
  const q = params.query.trim().toLowerCase()

  const base: SearchResult = {
    search: false,
    query: q,
    type,
    order,
    limit,
    offset,
    count: 0,
    timer: 0,
    results: [],
  }
  if (!isSearchable(q, type)) return base

  const needle = type === 'hex' ? q.replace(/^#/, '') : q
  let hits = colors.filter((c) =>
    type === 'hex' ? c.hex.slice(1).includes(needle) : c.name.toLowerCase().includes(needle),
  )

  if (order === 'relevant') {
    const key = (c: RegistryColor) => (type === 'hex' ? c.hex.slice(1) : c.name.toLowerCase())
    hits = hits
      .map((c) => ({ c, d: levenshtein(needle, key(c)) }))
      .sort((a, b) => a.d - b.d || a.c.name.localeCompare(b.c.name))
      .map((x) => x.c)
  } else {
    const [col, dir] = order.split('-') as [SortColumn, 'asc' | 'desc']
    const sign = dir === 'desc' ? -1 : 1
    hits = [...hits].sort((a, b) => {
      switch (col) {
        case 'name':
          return sign * a.name.localeCompare(b.name)
        case 'hex':
          return sign * a.hex.localeCompare(b.hex)
        case 'reg':
          return sign * cmp(a.registration, b.registration)
        case 'exp':
          return sign * cmp(a.expiration, b.expiration)
      }
    })
  }

  return {
    ...base,
    search: hits.length > 0,
    count: hits.length,
    results: hits.slice(offset, offset + limit),
    timer: performance.now() - start,
  }
}

/** Look a color up by slug or by hex ("#ff0000" / "ff0000"). */
export function findColor(colors: RegistryColor[], key: string): RegistryColor | undefined {
  const k = key.trim().toLowerCase()
  if (isHex(k)) {
    const hex = normalizeHex(k)
    return colors.find((c) => c.hex === hex)
  }
  return colors.find((c) => c.slug === k)
}

export function formatDate(iso: string | null, reserved = false): string {
  if (reserved) return 'Reserved'
  if (!iso) return 'Lifetime'
  const [y, m, d] = iso.split('-')
  return `${y}/${m}/${d}`
}

export function formatCount(n: number): string {
  return n.toLocaleString('en-US')
}

export const PAGE_SIZE = 10

/** The page numbers to offer around `page`: a window of `size`, clamped to 1..pages. */
export function pageWindow(page: number, pages: number, size = 5): number[] {
  const count = Math.max(0, Math.min(size, pages))
  const start = Math.max(1, Math.min(page - Math.floor(size / 2), pages - size + 1))
  return Array.from({ length: count }, (_, i) => start + i)
}

/**
 * The registry page keeps its whole state in the URL so a search can be
 * shared: /registry?type=hex&q=%23ff00&sort=name-desc&page=3. Defaults are
 * left out. `?hex=` is the form the color picker links with.
 */
export interface SearchUrlState {
  type: SearchType
  query: string
  order: SortOrder
  page: number
}

const SORT_RE = /^(name|hex|reg|exp)-(asc|desc)$/

export function readSearchUrl(params: URLSearchParams): SearchUrlState {
  const pickerHex = params.get('hex')
  const type: SearchType = pickerHex !== null || params.get('type') === 'hex' ? 'hex' : 'name'
  const rawQuery = pickerHex ?? params.get('q') ?? ''
  const query = rawQuery ? (type === 'hex' ? sanitizeHexInput(rawQuery) : sanitizeNameInput(rawQuery)) : ''
  const sort = params.get('sort') ?? ''
  const order: SortOrder = SORT_RE.test(sort) ? (sort as SortOrder) : 'relevant'
  const page = Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1)
  return { type, query, order, page }
}

export function writeSearchUrl(state: SearchUrlState): URLSearchParams {
  const p = new URLSearchParams()
  if (state.type === 'hex') p.set('type', 'hex')
  if (state.query) p.set('q', state.query)
  if (state.order !== 'relevant') p.set('sort', state.order)
  if (state.page > 1) p.set('page', String(state.page))
  return p
}
